<template>
  <div :style="cssProps">
    <v-toolbar elevation="1" density="compact" :color="$store.state.currentMoc.formColor">
      <v-toolbar-title>
        {{ $t('Tasks') }}
        <v-tooltip v-if="!show" :text="showAllTasks ? $t('Hide') : $t('Show')" location="bottom">
          <template v-slot:activator="{ props }">
            <v-btn icon @click="showAllTasks = !showAllTasks" v-bind="props">
                <v-icon>{{ showAllTasks ? 'mdi-eye-off-outline' : 'mdi-eye-outline' }}</v-icon>
              </v-btn>
          </template>
        </v-tooltip>
      </v-toolbar-title>
      <template v-slot:append>
       <v-menu :nudge-width="100" v-if="!isClosed">
         <template v-slot:activator="{ props }">
          <v-toolbar-title v-bind="props">
            <span>{{ displayFilterValue }} ({{ filteredTasks.length }})</span>
            <v-btn text icon>
              <v-icon dark>mdi-filter-variant</v-icon>
            </v-btn>
          </v-toolbar-title>
          </template>
          <v-list>
            <v-list-item v-for="(item, index) in sortBy" :key="index" @click="filterBy(item)">
              <v-list-item-title v-text="item.value"></v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
        <csv-export :data="filteredTasks" :exportConfig="csvExportConfig"></csv-export>
      </template>
    </v-toolbar>

    <v-data-table
      v-if="showAllTasks"
      hide-default-footer
      :headers="taskHeaders"
      :items="filteredTasks"
      item-value="id"
      density="compact"
      :items-per-page="-1"
      show-expand
      dense
      class="background-color"
      multi-sort
      :sort-by="[{ key: 'step', order: 'asc' }, { key: 'taskListType.value', order: 'asc' }]"
      :sort-desc="[false, false]"
      disable-pagination
    >
      <template v-slot:item="{ item, internalItem, isExpanded, toggleExpand }">
        <tr class="rowone" :key="item.id + 'row1'">
          <td colspan="6" @click="toggleExpand(internalItem)" class="text-subtitle-1 titlerow task-no-border">
            <strong>{{ item.name }}</strong>
          </td>
        </tr>
        <tr class="rowtwo" :key="item.id + 'row2'">
          <td @click="toggleExpand(internalItem)" class="text-subtitle-1"></td>
          <td @click="toggleExpand(internalItem)" class="text-subtitle-1">{{ item.step }}</td>
          <td @click="toggleExpand(internalItem)" class="text-subtitle-1">{{ (item.assignedTo || {}).displayName }}</td>
          <td @click="toggleExpand(internalItem)" class="text-subtitle-1">{{ item.taskListType.value }}</td>
          <td @click="toggleExpand(internalItem)" class="text-subtitle-1">{{ item.status ? item.status.name : null }}</td>
          <td @click="toggleExpand(internalItem)" class="text-subtitle-1">{{ $filters.DateFilter(item.completedDate) }}</td>
        </tr>
        <tr class="rowthree" :key="item.id + 'row3'">
          <td colspan="6" style="padding-bottom:20px; white-space: pre-wrap;">
            <pre style="white-space: pre-wrap;">{{ item.comment }}</pre>
          </td>
        </tr>
      </template>

      <template v-slot:expanded-row="{ columns, item }">
        <td :colspan="columns.length">
          <v-card class="white">
            <v-container fluid>
              <v-row>
                <v-col cols="12" lg="4" sm="5">
                  <peoplePicker :label="$t('Completed_By')" readonly :user="item.completedBy"></peoplePicker>
                </v-col>
                <v-col cols="12" lg="4" sm="5">
                  <peoplePicker :label="$t('Last_Modified_By')" readonly :user="item.modifiedBy"></peoplePicker>
                </v-col>
                <v-col cols="12" lg="3" sm="5">
                  <v-textarea
                    :label="$t('Modified_On')"
                    rows="1"
                    dense
                    readonly
                    :model-value="$filters.DateTimeFilter(item.modifiedByDate)"
                  ></v-textarea>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" sm="5">
                  <v-textarea :label="$t('Comments')" auto-grow rows="1" readonly :model-value="item.comment" dense></v-textarea>
                </v-col>
              </v-row>

              <v-row>
                <v-col cols="12" sm="6">
                  <v-card>
                    <v-list dense>
                      <v-list-group>                        
                        <template v-slot:activator="{ props }">                          
                          <v-list-item v-bind="props">
                            <v-list-item-title>{{ $t('Supporting Links') }} ( {{ (item.supportingLinks || []).length }} )</v-list-item-title>
                          </v-list-item>
                        </template>
                        <v-list-item                          
                          v-for="(link, index) in item.supportingLinks"
                          :key="index"
                          @click.stop
                          :href="'//' + link.url"
                          target="_blank"
                        >
                          {{ link.name }}
                        </v-list-item>
                      </v-list-group>
                    </v-list>
                  </v-card>
                </v-col>

                <v-col cols="12" sm="6"
                  ><v-card>
                    <v-list dense>
                      <v-list-group>
                        <template v-slot:activator="{ props }">                          
                          <v-list-item v-bind="props">
                            <v-list-item-title>{{ $t('Standards') }} ( {{ (item.standards || []).length }} )</v-list-item-title>
                          </v-list-item>                          
                        </template>
                        <v-list-item
                          v-for="(standard, index) in item.standards"
                          :key="index"
                          @click.stop
                          :href="standard.url ? '//' + standard.url : '#'"
                          target="_blank"
                        >
                          {{ standard.name }}
                        </v-list-item>
                      </v-list-group>
                    </v-list>
                  </v-card>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12">
                  <task-history :history="item.history"></task-history>
                </v-col>
              </v-row>

              <template v-if="item.addedVia === 'manual'">
                <v-row justify="end" class="mr-2">
                  <span>
                    {{ $t('Manual_Task_Added_By') }}: {{ (item.insertedBy || {}).displayName }} -
                    {{ $filters.DateTimeFilter(item.insertedByDate) }}
                  </span>
                </v-row>
              </template>
            </v-container>
          </v-card>
        </td>
      </template>
    </v-data-table>

    <toaster ref="toaster"></toaster>
  </div>
</template>

<script>
import { FormStatus, Roles } from '@/common'
import { TaskListTypes } from '@/common/types'
import { mapState } from 'vuex'
import { multiPropertySort } from '@/common/arrayFunctions'
import Toaster from '@/components/Toaster'
import PeoplePicker from '@/components/peoplePicker/PeoplePicker'
import CsvExport from '@/components/CsvExport'
import TaskHistory from '@/components/task/TaskHistory'
export default {
  data() {
    return {
      expanded: [],
      singleExpand: false,
      dialog: false,
      filter: null,
      assignedToFilter: null,
      taskHeaders: [
        { title: this.$t('Task'), key: 'name' },
        { title: this.$t('Step'), key: 'step' },
        { title: this.$t('Assigned_To'), key: 'assignedTo.displayName' },
        { title: this.$t('Required_In_Order_To'), key: 'taskListType.value' },
        { title: this.$t('Status'), key: 'status.name' },
        { title: this.$t('Completed_Date'), key: 'completedDate' }
      ],
      mocStatusVal: 100,
      successCheck: [],
      csvExportConfig: {
        [this.$t('Task')]: 'name',
        [this.$t('Step')]: 'step',
        [this.$t('Assigned_To')]: 'assignedTo.displayName',
        [this.$t('Required_In_Order_To')]: 'taskListType.value',
        [this.$t('Status')]: 'status.name',
        [this.$t('Completed_Date')]: {
          property: 'completedDate',
          isDate: true
        }
      },
      showAllTasks: this.show
    }
  },
  props: {
    moc: {
      type: Object
    },
    tasks: {
      type: Array
    },
    readonly: {
      type: Boolean
    },
    roles: {
      type: Array,
      default: null
    },
    previousFormStatus: {
      type: Object
    },
    show: {
      type: Boolean,
      default: true
    }
  },
  components: {
    Toaster,
    PeoplePicker,
    CsvExport,
    TaskHistory
  },
  methods: {
    clicked(value) {
      this.expanded.push(value)
    },
    filterBy: function(item) {
      this.filter = item.key
    },
    formStatusValue: function(val) {
      switch (val.value) {
        case FormStatus.properties[FormStatus.NEW].value:
        case FormStatus.properties[FormStatus.DRAFT].value:
        case FormStatus.properties[FormStatus.SENTBACKFORCORRECTIONS].value:
          this.mocStatusVal = 1
          break
        case FormStatus.properties[FormStatus.BEINGREVISED].value:
          if (
            this.roles &&
            (this.previousFormStatus.value === FormStatus.properties[FormStatus.AVAILABLETOIMPLEMENT].value ||
              this.previousFormStatus.value === FormStatus.properties[FormStatus.AVAILABLEFORDECISION].value ||
              this.previousFormStatus.value === FormStatus.properties[FormStatus.PENDINGOWNERDECISION].value) &&
            (this.roles.includes(Roles.properties[Roles.INITIATOR].value) ||
              this.roles.includes(Roles.properties[Roles.COINITIATOR].value) ||
              this.roles.includes(Roles.properties[Roles.OWNER].value))
          ) {
            this.mocStatusVal = 1
          }
          break
        case FormStatus.properties[FormStatus.SUBMITTED].value:
          this.mocStatusVal = 2
          break
        case FormStatus.properties[FormStatus.AVAILABLEFORDECISION].value:
          this.mocStatusVal = 3
          break
        case FormStatus.properties[FormStatus.PENDINGOWNERDECISION].value:
          this.mocStatusVal = 4
          break
        case FormStatus.properties[FormStatus.AVAILABLETOIMPLEMENT].value:
          this.mocStatusVal = 5
          break
        case FormStatus.properties[FormStatus.CHANGEISINPLACEWITHCONSEQUENTIALACTIONSREQUIRED].value:
        case FormStatus.properties[FormStatus.CHANGEISFULLYIMPLEMENTED].value:
        case FormStatus.properties[FormStatus.TEMPORARYCHANGEISINPLACEWITHCONSEQUENTIALACTIONSREQUIRED].value:
          this.mocStatusVal = 6
          break
      }
    },
    setCurrentTasklistType: function() {
      this.selectedTasklistType = 2
      this.filter = 2
    },
    sortedTaskStatus: function(arrItemStatus) {
      let holdItemStatus = arrItemStatus.slice()
      let sortBy = [{ prop: 'displayOrder', direction: 1 }]
      if (holdItemStatus) {
        let sorted = multiPropertySort(sortBy, holdItemStatus)
        return sorted
      }
      return null
    }
  },
  computed: {
    ...mapState('currentUser', ['user']),
    ...mapState('currentMoc', ['formColor']),
    isClosed: function() {
      return (
        this.moc.status.value === FormStatus.properties[FormStatus.CLOSED].value ||
        this.moc.status.value === FormStatus.properties[FormStatus.CLOSEDVOIDED].value ||
        this.moc.status.value === FormStatus.properties[FormStatus.CLOSEDREJECTED].value
      )
    },
    cssProps() {
      switch (this.$store.state.currentMoc.formColor) {
        case 'backgroundNormal': {
          return {
            '--firstRowEven': '#E3F2FD',
            '--firstRowOdd': '#bbdefb',
            '--secondRowEven': '#bbdefb',
            '--thirdRowOdd': '#bbdefb',
            '--bgcolor': '#E3F2FD'
          }
        }
        case 'backgroundUnauthorizedExpired': {
          return {
            '--firstRowEven': '#FBE9E7',
            '--firstRowOdd': '#ECDAD8',
            '--secondRowEven': '#ECDAD8',
            '--thirdRowOdd': '#ECDAD8',
            '--bgcolor': '#FBE9E7'
          }
        }
        case 'backgroundUnauthorizedExpired2': {
          return {
            '--firstRowEven': '#FFEBEE',
            '--firstRowOdd': '#F0DCDF',
            '--secondRowEven': '#F0DCDF',
            '--thirdRowOdd': '#F0DCDF',
            '--bgcolor': '#FFEBEE'
          }
        }
        case 'backgroundUnauthorizedExpired3': {
          return {
            '--firstRowEven': '#F3E5F5',
            '--firstRowOdd': '#DED8E7',
            '--secondRowEven': '#DED8E7',
            '--thirdRowOdd': '#DED8E7',
            '--bgcolor': '#F3E5F5'
          }
        }
        default:
          return {
            '--firstRowEven': '#E3F2FD',
            '--firstRowOdd': '#bbdefb',
            '--secondRowEven': '#bbdefb',
            '--thirdRowOdd': '#bbdefb',
            '--bgcolor': '#E3F2FD'
          }
      }
    },
    sortBy: function() {
      let arr = Object.keys(TaskListTypes.properties).map(k => TaskListTypes.properties[k])
      // add All to the mix
      // arr.splice(0, 0, { key: null, value: 'All' })

      return arr
    },
    defaultFilter: function() {
      switch (this.moc.status.value) {
        case FormStatus.properties[FormStatus.NEW].value:
        case FormStatus.properties[FormStatus.DRAFT].value:
        case FormStatus.properties[FormStatus.BEINGREVISED].value:
        case FormStatus.properties[FormStatus.SENTBACKFORCORRECTIONS].value: {
          return { key: TaskListTypes.SUBMIT }
        }
        case FormStatus.properties[FormStatus.SUBMITTED].value: {
          return { key: TaskListTypes.SENDFORDECISION }
        }
        case FormStatus.properties[FormStatus.AVAILABLEFORDECISION].value: {
          return { key: TaskListTypes.ENABLEOWNERDECISIONTOOCCUR }
        }
        case FormStatus.properties[FormStatus.PENDINGOWNERDECISION].value: {
          return { key: TaskListTypes.PROCEEDWITHIMPLEMENTATION }
        }
        case FormStatus.properties[FormStatus.AVAILABLETOIMPLEMENT].value: {
          return { key: TaskListTypes.CONFIRMCHANGEISINPLACE }
        }
        case FormStatus.properties[FormStatus.CHANGEISINPLACEWITHCONSEQUENTIALACTIONSREQUIRED].value:
        case FormStatus.properties[FormStatus.CHANGEISFULLYIMPLEMENTED].value:
        case FormStatus.properties[FormStatus.TEMPORARYCHANGEISINPLACEWITHCONSEQUENTIALACTIONSREQUIRED].value: {
          return { key: TaskListTypes.CONFIRMCHANGEISFULLYIMPLEMENTED }
        }
      }
      return { key: TaskListTypes.ALL }
    },
    displayFilterValue: function() {
      if (this.filter) {
        return TaskListTypes.properties[this.filter].value
      }
      return TaskListTypes.properties[TaskListTypes.ALL].value
    },
    filteredTasks: function() {
      let results = this.tasks
      if (this.filter) {
        if (!this.isClosed) {
          if (TaskListTypes.properties[this.filter].key === TaskListTypes.properties[TaskListTypes.ALL].key) {
            results = this.tasks
          } else {
            results = this.tasks.filter(t => t.taskListType.key === TaskListTypes.properties[this.filter].key)
          }
        }
      }

      return results.map(s => ({ ...s, completedDate: this.$filters.DateFilter(s.completedDate) }))
    }
  },
  mounted() {
    this.filterBy(this.defaultFilter)
  }
}
</script>

<style scoped>
.v-data-table tbody tr:not(:last-child) td:last-child {
  border-bottom: 0px solid rgba(255, 255, 255, 0.12);
}

.commentLabel {
  font-weight: bold;
}

.pencilCell {
  background-color: var(--bgcolor);
}

.background-color {
  background-color: var(--bgcolor);
}

.rowone {
  background-color: var(--firstRowOdd);
}

.rowone:nth-of-type(odd) {
  background-color: var(--firstRowOdd);
}

.rowone:nth-of-type(even) {
  background-color: var(--firstRowEven);
}
.rowtwo:nth-of-type(odd) {
  background-color: var(--secondRowOdd);
}
.rowtwo:nth-of-type(even) {
  background-color: var(--secondRowEven);
}
.rowthree:nth-of-type(odd) {
  background-color: var(--thirdRowOdd);
}
.rowthree:nth-of-type(even) {
  background-color: var(--thirdRowEven);
}
.task-no-border{
  border:0 !important;
}
</style>
